/**
 * Footer Component
 * Site footer with brokerage info, agent details, contact, and legal information
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from '@/routes/paths';
import { formatVersion, commitUrl, formatBuildDateTimeLocal } from '@/lib/version';
import { useFooterAgentInfo } from '@/hooks';
import { telHref } from '@/utils';


// Hard-coded fallbacks are personal-to-Shayaan and permanent: his
// name, title, mobile, and agent license. These stay even if the
// public-profile API hasn't responded yet — they can't go stale
// without an act of legal name change.
//
// Personal-agent fallbacks now come exclusively off the backend
// (which can read from .env). No personal info is bundled into
// the shipped JS — empty values just hide the corresponding row
// until /agent-config/public responds. The backend serves env-
// driven defaults when the agent_config table is empty.
const AGENT_FALLBACKS = {
    agentName: '',
    agentTitle: '',
    agentPhone: '',
    agentLicense: '',
} as const;


/**
 * Splits a multi-line address into individual <p> lines. Accepts a
 * single string with commas ("3228 Highway 27, South Brunswick, NJ
 * 08824") OR explicit \n separators, since the office_address column
 * is free-form on the contact card.
 */
function addressLines(raw: string | null | undefined): string[] {
    if (!raw) return [];
    const byNewline = raw.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    if (byNewline.length > 1) return byNewline;
    // No explicit newlines — split on the first comma so a street
    // address always sits on its own line above the city / state / zip.
    const [street, ...rest] = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (!street || rest.length === 0) return [raw.trim()];
    return [street, rest.join(', ')];
}


export function Footer() {
    const [showRealtorModal, setShowRealtorModal] = useState(false);
    const [showFairHousingModal, setShowFairHousingModal] = useState(false);
    const info = useFooterAgentInfo();

    // Agent-side: hard-coded fallbacks are safe (personal + permanent).
    const agentName = info?.agent_name?.toUpperCase() || AGENT_FALLBACKS.agentName;
    const agentTitle = info?.agent_title || AGENT_FALLBACKS.agentTitle;
    const agentPhone = info?.agent_phone || AGENT_FALLBACKS.agentPhone;
    const agentLicense = info?.agent_license || AGENT_FALLBACKS.agentLicense;

    // Broker-side: NO fallback. Empty values hide the row entirely so
    // stale company names from a previous brokerage can't leak onto
    // the page after Shayaan switches affiliations.
    const brokerName = (info?.broker_name || '').trim();
    const brokerPhone = (info?.broker_phone || '').trim();
    const brokerLicense = (info?.broker_license_number || '').trim();
    const officeLines = addressLines(info?.broker_office_address);

    return (
        <footer className="bg-secondary text-white mt-auto">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Main Footer Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                    {/* Logo and Brokerage Info */}
                    <div>
                        <Link to={PATHS.HOME}>
                            <div className="w-40 h-[100px] relative mb-4">
                                <img
                                    src="/Images/logo-transparent.gif"
                                    alt="Real Estate With Shayaan"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        </Link>
                        {brokerName && (
                            <div className="text-sm text-gray-300">
                                <BrokerName name={brokerName} />
                            </div>
                        )}
                    </div>

                    {/* Agent Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-primary mb-3">{agentName}</h3>
                        <p className="text-sm text-gray-300 mb-2">{agentTitle}</p>
                        {officeLines.length > 0 && (
                            <div className="text-sm text-gray-300 space-y-1">
                                {officeLines.map((line, i) => (
                                    <p key={`${line}-${i}`}>{line}</p>
                                ))}
                            </div>
                        )}
                        <AgreementsDropdown />
                        <div className="mt-2">
                            <Link
                                to={PATHS.POSTS}
                                className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:text-primary/80 transition-colors"
                            >
                                Articles
                                <span className="text-xs">›››</span>
                            </Link>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-primary mb-3">Contact</h3>
                        <div className="text-sm text-gray-300 space-y-2">
                            {/* Broker's Office row hidden when no phone is on file —
                                otherwise we'd render an empty "Broker's Office:" with
                                nothing after it on the day Shayaan changes brokerages. */}
                            {brokerPhone && (
                                <p>
                                    <span className="text-gray-400">Broker&apos;s Office: </span>
                                    <a href={telHref(brokerPhone)} className="hover:text-primary transition-colors">
                                        {brokerPhone}
                                    </a>
                                </p>
                            )}
                            <p>
                                <span className="text-gray-400">Mobile: </span>
                                <a href={telHref(agentPhone)} className="hover:text-primary transition-colors">
                                    {agentPhone}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Licenses. Agent license is always shown (it's tied to
                    the person, not the broker); brokerage license is
                    only shown when present so a switch between firms
                    doesn't display the old number while the new one is
                    being entered. */}
                <div className="border-t border-primary/20 pt-6 mb-6">
                    <div className="text-sm text-gray-400 text-center space-y-1">
                        <p>
                            Agent License #: {agentLicense}
                            {brokerLicense && (
                                <> | Brokerage License #: {brokerLicense}</>
                            )}
                        </p>
                    </div>
                </div>

                {/* Logos and Disclaimer */}
                <div className="border-t border-primary/20 pt-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
                        {/* Fair Housing and Realtor Logos */}
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setShowFairHousingModal(true)}
                                className="w-12 h-12 relative block hover:opacity-80 transition-opacity cursor-pointer"
                                title="NJ Law Against Discrimination"
                            >
                                <img
                                    src="/Images/fair-housing-logo.png"
                                    alt="Equal Housing Opportunity"
                                    className="w-full h-full object-contain"
                                    style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(18%) saturate(579%) hue-rotate(6deg) brightness(93%) contrast(86%)' }}
                                />
                            </button>
                            <button
                                onClick={() => setShowRealtorModal(true)}
                                className="w-12 h-12 relative block hover:opacity-80 transition-opacity cursor-pointer"
                                title="View Realtor Profile"
                            >
                                <img
                                    src="/Images/realtor-logo.png"
                                    alt="Realtor"
                                    className="w-full h-full object-contain"
                                    style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(18%) saturate(579%) hue-rotate(6deg) brightness(93%) contrast(86%)' }}
                                />
                            </button>
                        </div>

                        {/* Independently Owned Disclaimer */}
                        <p className="text-xs text-gray-400 text-center md:text-right">
                            Each Office is Independently Owned and Operated
                        </p>
                    </div>

                    {/* Copyright & Privacy Policy */}
                    <div className="text-center text-sm text-gray-400 flex flex-col sm:flex-row items-center justify-center gap-2">
                        <p>Copyright © {new Date().getFullYear()}, Real Estate With Shayaan. All rights reserved.</p>
                        <span className="hidden sm:inline text-gray-500">|</span>
                        <Link to="/privacy-policy" className="hover:text-primary transition-colors">
                            Privacy Policy
                        </Link>
                    </div>

                    {/* Build version (date + short commit SHA) */}
                    <VersionStamp />
                </div>
            </div>

            {/* Fair Housing Modal */}
            {showFairHousingModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowFairHousingModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] mx-4 relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setShowFairHousingModal(false)}
                            className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
                            aria-label="Close"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <iframe
                            src="https://www.nj.gov/dca/home/act.shtml"
                            className="w-full h-full rounded-lg"
                            title="NJ Law Against Discrimination"
                        />
                    </div>
                </div>
            )}

            {/* Realtor Profile Modal */}
            {showRealtorModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowRealtorModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] mx-4 relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setShowRealtorModal(false)}
                            className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
                            aria-label="Close"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <iframe
                            src="https://directories.apps.realtor/memberDetail/?personId=4950851&officeStreetCountry=US&memberLastName=Siddiqui"
                            className="w-full h-full rounded-lg"
                            title="Realtor Profile"
                        />
                    </div>
                </div>
            )}
        </footer>
    );
}

// ─── Broker Name (two-line render) ───
//
// The old hardcoded markup had "CENTURY 21" in primary color on the
// first line and "Abrams & Associates" in white on the second. The
// brokerage company contact stores the name as one string ("CENTURY
// 21 Abrams & Associates"). We try a couple of natural splits so the
// visual styling survives: explicit \n first, then the first space
// after a digit (works for the C21 case), otherwise a single line.

function BrokerName({ name }: { name: string }) {
    const lines = (() => {
        if (!name) return [''];
        const byNewline = name.split(/\n+/).map((s) => s.trim()).filter(Boolean);
        if (byNewline.length > 1) return byNewline;
        // "CENTURY 21 Abrams & Associates" → ["CENTURY 21", "Abrams & Associates"]
        const [, prefix, rest] = name.match(/^([A-Z0-9][^a-z]*\d+)\s+(.+)$/) ?? [];
        if (prefix && rest) return [prefix.trim(), rest.trim()];
        return [name];
    })();
    if (lines.length === 1) {
        return <p className="font-semibold text-primary">{lines[0]}</p>;
    }
    return (
        <>
            <p className="font-semibold text-primary">{lines[0]}</p>
            <p className="font-semibold">{lines.slice(1).join(' ')}</p>
        </>
    );
}


// ─── Agreements Dropdown (hover on desktop, tap on mobile) ───

// Mirrors the catalogue on /agreements: only `available` entries are
// listed, the rest stay here to be switched on when their paperwork ships.
const AGREEMENTS = [
    { name: "Buyer's Agreement", path: PATHS.OUR_AGREEMENTS, available: true },
    { name: "Seller's Agreement", path: PATHS.OUR_AGREEMENTS, available: false },
    { name: 'Rental Agreement', path: PATHS.OUR_AGREEMENTS, available: false },
    { name: 'Tenant Agreement', path: PATHS.OUR_AGREEMENTS, available: false },
];
const AVAILABLE_AGREEMENTS = AGREEMENTS.filter((agreement) => agreement.available);

function AgreementsDropdown() {
    return (
        <div className="relative inline-block group mt-3">
            <Link
                to={PATHS.OUR_AGREEMENTS}
                className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:text-primary/80 transition-colors"
            >
                Agreements
                <span className="text-xs">›››</span>
            </Link>

            {/* Dropdown — visible on hover (desktop only) */}
            <div className="hidden group-hover:block absolute left-0 top-full mt-1 w-56 bg-white text-gray-900 rounded-lg shadow-xl border border-gray-100 overflow-hidden z-20">
                {AVAILABLE_AGREEMENTS.map((agreement) => (
                    <Link
                        key={agreement.name}
                        to={agreement.path}
                        className="block px-4 py-2.5 text-sm text-gray-900 hover:bg-gray-50 transition-colors"
                    >
                        {agreement.name}
                    </Link>
                ))}
            </div>
        </div>
    );
}

function VersionStamp() {
    const version = formatVersion();
    if (!version) return null;
    const href = commitUrl();
    const localTime = formatBuildDateTimeLocal();
    const title = localTime ? `Built ${localTime}` : undefined;
    const stamp = (
        <span title={title} className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">
            {version}
        </span>
    );
    return (
        <div className="mt-2 text-center">
            {href ? (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors"
                >
                    {stamp}
                </a>
            ) : (
                stamp
            )}
        </div>
    );
}

export default Footer;